import React, { useState } from 'react';
import WhatsAppLink from '../global/WhatsAppLink';
import { CTA_IDS, CTA_LOCATIONS } from '../../lib/analytics.constants';
import { useStagger } from '../../hooks/useInView';

const faqs = [
    {
        question: "¿Qué diferencia a eien de una software factory?",
        answer: "En eien no solo desarrollamos software: pensamos con vos, definimos el producto, priorizamos funcionalidades y acompañamos toda la vida del proyecto. Somos tu equipo tecnológico, no un proveedor puntual que solo ejecuta tareas.",
    },
    {
        question: "¿Qué tipo de proyectos construyen?",
        answer: "Desarrollamos software a medida, apps web y mobile, sistemas internos, MVPs y productos SaaS. Trabajamos con startups y empresas que necesitan tecnología seria para operar, escalar y automatizar procesos.",
    },
    {
        question: "¿Cómo saben cuánto va a costar mi proyecto?",
        answer: "Primero entendemos tu idea y tus objetivos. Luego hacemos un Discovery para definir alcance, funcionalidades clave y roadmap. Con eso te entregamos un plan con costos, tiempos estimados y el modelo de trabajo recomendado.",
    },
    {
        question: "¿Cuánto tarda construir un MVP o un sistema?",
        answer: "Depende del alcance, pero en general: un MVP o SaaS simple toma entre 12 y 20 semanas; un sistema interno o integraciones, entre 3 y 6 meses. Siempre trabajamos por sprints con entregas constantes.",
    },
    {
        question: "¿Qué necesito traer antes de empezar?",
        answer: "Nada técnico. Solo necesitamos tu idea o problema a resolver, tus objetivos de negocio y cómo imaginás que debería funcionar. Nosotros te guiamos en todo el proceso, desde definir el alcance hasta lanzar el producto.",
    },
    {
        question: "¿Qué pasa si no sé exactamente qué quiero construir?",
        answer: "No pasa nada. Te ayudamos a validar, priorizar y convertir tu idea en un MVP funcional. Nuestro equipo define con vos el alcance, las funcionalidades esenciales y la mejor estrategia para lanzar.",
    },
    {
        question: "¿Qué modelos de trabajo ofrecen?",
        answer: "Trabajamos con tres modelos: Proyecto, con alcance cerrado y pago por etapas; Partnership, donde somos tu equipo tecnológico continuo por un fee mensual fijo; y Revenue Share, donde pagás un fee reducido más entre el 1% y 18% de ingresos según esfuerzo y alcance.",
    },
    {
        question: "¿Qué soporte tengo después del lanzamiento?",
        answer: "Todos los proyectos incluyen garantía de correcciones. Además, podés sumarte a un plan mensual de soporte y evolución continua: mantenimiento, mejoras funcionales, optimización, métricas y nuevas versiones.",
    },
    {
        question: "¿Puedo empezar con algo chico y después ampliar?",
        answer: "Sí. Siempre construimos con arquitectura escalable, lo que te permite empezar con un MVP y luego evolucionar el producto sin tener que rehacer todo. Diseñamos pensando en tu crecimiento.",
    },
    {
        question: "¿Necesito pagar todo desde el inicio?",
        answer: "No. Según el modelo elegido: en Proyecto pagás por etapas según avance; en Partnership es un fee mensual fijo; y en Revenue Share pagás un fee reducido más la participación en ingresos.",
    },
    {
        question: "¿Cuánto cuesta desarrollar software a medida en Paraguay?",
        answer: "El costo varía según el alcance. En eien tenemos tres modelos: Proyecto (alcance cerrado, pago por etapas), Partnership (equipo continuo por fee mensual) y Revenue Share (fee reducido + participación en ingresos). Hacemos un Discovery sin costo para definir alcance y presupuesto real.",
    },
    {
        question: "¿Trabajan con empresas de todo el Paraguay?",
        answer: "Sí. Trabajamos con empresas de todo el Paraguay — Asunción, Central, Ciudad del Este, Encarnación y el interior — de forma 100% remota. También atendemos clientes en Argentina, Uruguay, Chile y toda LATAM.",
    },
    {
        question: "¿Desarrollan apps móviles y sistemas de gestión?",
        answer: "Sí. Construimos apps móviles (iOS y Android), sistemas de gestión empresarial, plataformas web, SaaS y automatización de procesos para empresas y startups en Paraguay. Cada solución se diseña a medida.",
    },
];

export default function FAQ() {
    const [selected, setSelected] = useState(0);
    const [openMobile, setOpenMobile] = useState(null);
    const { ref: headRef, itemStyle: headStyle } = useStagger({ count: 2, stagger: 70 });
    const { ref: listRef, itemStyle } = useStagger({ count: faqs.length, stagger: 28, duration: 400 });

    return (
        <section className="py-28 px-4 bg-gray-50 dark:bg-[#0a0a0a]">
            <style>{`
                @keyframes faqFadeIn {
                    from { opacity: 0; transform: translateY(8px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                .faq-fade { animation: faqFadeIn 0.22s ease-out; }

                .faq-scroll::-webkit-scrollbar {
                    width: 4px;
                }
                .faq-scroll::-webkit-scrollbar-track {
                    background: transparent;
                    border-radius: 99px;
                }
                .faq-scroll::-webkit-scrollbar-thumb {
                    background: rgba(17, 179, 109, 0.3);
                    border-radius: 99px;
                }
                .faq-scroll::-webkit-scrollbar-thumb:hover {
                    background: rgba(17, 179, 109, 0.6);
                }
                .dark .faq-scroll::-webkit-scrollbar-thumb {
                    background: rgba(17, 179, 109, 0.25);
                }
                .dark .faq-scroll::-webkit-scrollbar-thumb:hover {
                    background: rgba(17, 179, 109, 0.55);
                }
            `}</style>

            <div className="max-w-6xl mx-auto">

                {/* Header */}
                <div ref={headRef} className="mb-12">
                    <p style={headStyle(0)} className="w-fit text-[10px] font-medium uppercase tracking-[0.2em] text-[#11b36d] bg-[#11b36d]/10 border border-[#11b36d]/20 rounded-full px-3 py-1 mb-5">FAQ</p>
                    <h2 style={headStyle(1)} className="text-3xl font-thin tracking-tighter sm:text-4xl text-[#0b2b40] dark:text-white">
                        Preguntas <span className="text-gradient text-short">frecuentes</span>
                    </h2>
                </div>

                {/* ── DESKTOP: master-detail ── */}
                <div className="hidden md:grid grid-cols-5 gap-3 items-start">

                    {/* Question list — 2/5 */}
                    <div ref={listRef} className="col-span-2 flex flex-col gap-1 max-h-[540px] overflow-y-auto faq-scroll pr-2">
                        {faqs.map((faq, i) => (
                            <button
                                key={i}
                                style={itemStyle(i)}
                                onClick={() => setSelected(i)}
                                className={`text-left px-4 py-3.5 rounded-xl flex items-start gap-3 transition-all duration-200 border ${
                                    selected === i
                                        ? 'bg-white dark:bg-[#111111] border-[#11b36d]/35 dark:border-[#11b36d]/35'
                                        : 'border-transparent hover:bg-white dark:hover:bg-[#111111] hover:border-gray-100 dark:hover:border-white/[0.07]'
                                }`}
                            >
                                <span className={`text-[10px] font-bold mt-1 shrink-0 tabular-nums ${
                                    selected === i ? 'text-[#11b36d]' : 'text-slate-300 dark:text-slate-600'
                                }`}>
                                    {String(i + 1).padStart(2, '0')}
                                </span>
                                <span className={`text-sm leading-snug transition-colors duration-200 ${
                                    selected === i
                                        ? 'text-[#0b2b40] dark:text-white font-medium'
                                        : 'text-slate-500 dark:text-slate-400'
                                }`}>
                                    {faq.question}
                                </span>
                            </button>
                        ))}
                    </div>

                    {/* Answer panel — 3/5 */}
                    <div className="col-span-3 bg-[#f4fdf8] dark:bg-[#0b1a10] border border-[#11b36d]/40 rounded-2xl p-8 flex flex-col min-h-[540px] shadow-[0_0_28px_rgba(17,179,109,0.07)] dark:shadow-[0_0_32px_rgba(17,179,109,0.12)] relative overflow-hidden">

                        {/* Watermark number */}
                        <span className="absolute top-4 right-6 text-[9rem] font-bold leading-none select-none text-[#11b36d]/[0.04] dark:text-[#11b36d]/[0.06] pointer-events-none">
                            {String(selected + 1).padStart(2, '0')}
                        </span>

                        <div key={selected} className="faq-fade flex flex-col flex-grow">
                            <div className="inline-flex items-center justify-center p-2.5 rounded-lg bg-[#11b36d]/15 border border-[#11b36d]/30 text-[#11b36d] mb-5 w-fit">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>

                            <h3 className="text-lg font-medium text-[#0b2b40] dark:text-white mb-4 leading-snug">
                                {faqs[selected].question}
                            </h3>
                            <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed flex-grow">
                                {faqs[selected].answer}
                            </p>
                        </div>

                        {/* Footer nav */}
                        <div className="mt-6 pt-5 border-t border-[#11b36d]/15 flex items-center justify-between">
                            <span className="text-xs text-slate-400 dark:text-slate-500 tabular-nums">
                                {selected + 1} <span className="opacity-40">/</span> {faqs.length}
                            </span>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setSelected((selected - 1 + faqs.length) % faqs.length)}
                                    className="w-7 h-7 rounded-full border border-[#11b36d]/30 flex items-center justify-center text-[#11b36d] hover:bg-[#11b36d]/10 transition-colors duration-200"
                                    aria-label="Anterior"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                    </svg>
                                </button>
                                <button
                                    onClick={() => setSelected((selected + 1) % faqs.length)}
                                    className="w-7 h-7 rounded-full border border-[#11b36d]/30 flex items-center justify-center text-[#11b36d] hover:bg-[#11b36d]/10 transition-colors duration-200"
                                    aria-label="Siguiente"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── MOBILE: accordion ── */}
                <div className="md:hidden space-y-2">
                    {faqs.map((faq, i) => (
                        <div
                            key={i}
                            className="bg-white dark:bg-[#111111] rounded-2xl border border-gray-100 dark:border-white/[0.07] overflow-hidden transition-colors duration-200 hover:border-[#11b36d]/30"
                        >
                            <button
                                onClick={() => setOpenMobile(openMobile === i ? null : i)}
                                className="w-full px-5 py-4 text-left flex items-center justify-between gap-4"
                            >
                                <div className="flex items-center gap-3">
                                    <span className="text-[10px] font-bold text-[#11b36d] shrink-0 tabular-nums">
                                        {String(i + 1).padStart(2, '0')}
                                    </span>
                                    <span className="text-sm font-medium text-[#0b2b40] dark:text-white leading-snug">
                                        {faq.question}
                                    </span>
                                </div>
                                <span className={`shrink-0 transition-transform duration-300 text-[#11b36d] ${openMobile === i ? 'rotate-180' : ''}`}>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openMobile === i ? 'max-h-64' : 'max-h-0'}`}>
                                <p className="px-5 pb-4 text-sm text-slate-500 dark:text-slate-400 leading-relaxed border-t border-gray-50 dark:border-white/[0.04] pt-3">
                                    {faq.answer}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>

                {/* CTA */}
                <div className="mt-10 bg-white dark:bg-[#111111] rounded-2xl p-8 border border-gray-100 dark:border-white/[0.07] flex flex-col sm:flex-row items-center justify-between gap-6">
                    <div>
                        <p className="font-medium text-[#0b2b40] dark:text-white mb-1">¿Tenés otra pregunta?</p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">Contanos tu idea, te armamos roadmap, costo y plan de pagos.</p>
                    </div>
                    <WhatsAppLink
                        ctaId={CTA_IDS.FAQ_WHATSAPP}
                        ctaLocation={CTA_LOCATIONS.FAQ}
                        message="Hola, quiero evaluar mi proyecto"
                        className="shrink-0 inline-flex items-center gap-2 bg-[#11b36d] text-white px-6 py-3 rounded-full font-semibold text-sm hover:bg-[#0ea060] shadow-[0_2px_8px_rgba(17,179,109,0.22),inset_0_1px_0_rgba(255,255,255,0.12)] hover:shadow-[0_4px_14px_rgba(17,179,109,0.32),inset_0_1px_0_rgba(255,255,255,0.12)] active:scale-[0.97] transition-all duration-200"
                    >
                        Escribinos
                    </WhatsAppLink>
                </div>

            </div>
        </section>
    );
}
